import Phaser from 'phaser';
import { EventBus, Events } from '../managers';
import { ENEMY_SPRITES, PLAYER_SPRITES, BACKGROUNDS, type SpriteConfig } from './spriteConfig';
import { SPRITE_ANCHORS, isAutoAnchorsEnabled } from './spriteAnchors.generated';
import { GridFormationLayout, type ArenaBounds, type SlotWorldPosition } from './layout/GridFormationLayout';

/** When the auto-anchors flag is on, swap the hand-tuned center/shadow offsets
 *  for values derived from per-sprite alpha analysis. shadowScale stays as
 *  hand-tuned regardless. */
function resolveCenterOffsetX(config: SpriteConfig): number {
  if (isAutoAnchorsEnabled()) {
    const a = SPRITE_ANCHORS[config.key];
    if (a) return a.centerOffsetX;
  }
  return config.centerOffsetX;
}

function resolveShadowOffsets(config: SpriteConfig): { centerOffsetX: number; shadowOffsetX: number; shadowOffsetY: number } {
  if (isAutoAnchorsEnabled()) {
    const a = SPRITE_ANCHORS[config.key];
    if (a) return a;
  }
  return {
    centerOffsetX: config.centerOffsetX,
    shadowOffsetX: config.shadowOffsetX,
    shadowOffsetY: config.shadowOffsetY,
  };
}
import type { CombatantPosition, CombatantPositionsPayload, CombatSceneData, AnimationDef } from '../types';
import { getPendingSceneData } from '../main';
import { playPreset, playCastPreset, flashCasterGlow, playTransformPreset } from './effects/animationPresets';
import { elementColor } from '../utils/elementColors';
import { OutlinePipeline } from './effects/OutlinePipeline';
import { MordantOverlay, type MordantColorId } from './MordantOverlay';

interface CombatantSprite {
  sprite: Phaser.GameObjects.Sprite;
  id: string;
  isEnemy: boolean;
  config: SpriteConfig;
  /** Player-side combatant whose sprite came from ENEMY_SPRITES (a summoned minion).
   *  Drives MINION_SCALE_BOOST so backline-tuned minion sprites don't read as babies
   *  when standing in the strand line. */
  isMinion?: boolean;
  /** Enemy that was summoned mid-combat (e.g. Rift's void motes). When one of these
   *  dies, the remaining enemies reflow back to centered formation positions so the
   *  boss returns to its anchor instead of drifting. */
  isSummoned?: boolean;
}

type VisualLayer = 'lighting' | 'statusEffect' | 'activeTurn' | 'targeting' | 'flash';
interface LayerState {
  color: number;
  glowOnly?: boolean;
  glowRadius?: number;
  glowIntensity?: number;
  noGlow?: boolean;
}
/** Visual layer ranking — iterated low-to-high, LAST match wins.
 *  i.e. flash > targeting > activeTurn > statusEffect > lighting.
 *  activeTurn MUST outrank statusEffect (bleed/burn/poison/etc.) so the active-turn
 *  ring is never hidden behind a DoT glow. */
const LAYER_PRIORITY: VisualLayer[] = ['lighting', 'statusEffect', 'activeTurn', 'targeting', 'flash'];

/** Minion sprites (skeletons, etc.) are tuned for backline enemy formations.
 *  When summoned into the strand line, they need a scale boost to feel like
 *  party members, not babies. */
const MINION_SCALE_BOOST = 1.5;

/** Maps status effect types to persistent visual configs.
 *  Order = priority: later entries override earlier ones when multiple effects are active. */
const STATUS_EFFECT_VISUALS: Array<{
  type: string;
  color: number;
  glowRadius: number;
  glowIntensity: number;
}> = [
  // Passive buffs — subtle glow
  { type: 'spd_up', color: 0x88ffff, glowRadius: 4, glowIntensity: 0.3 },
  { type: 'martial_res_up', color: 0xcccccc, glowRadius: 4, glowIntensity: 0.3 },
  { type: 'rallied', color: 0xffcc44, glowRadius: 4, glowIntensity: 0.3 },
  { type: 'preparation', color: 0x88ccff, glowRadius: 4, glowIntensity: 0.3 },
  { type: 'raised_shield', color: 0xaabbcc, glowRadius: 4, glowIntensity: 0.3 },
  { type: 'brace', color: 0xaabb88, glowRadius: 4, glowIntensity: 0.3 },
  // Barriers — protective aura
  { type: 'brinewall', color: 0x3388dd, glowRadius: 5, glowIntensity: 0.4 },
  { type: 'tideguard', color: 0x1166bb, glowRadius: 5, glowIntensity: 0.4 },
  { type: 'iron_wall', color: 0xccaa44, glowRadius: 5, glowIntensity: 0.4 },
  // Active player buffs — stronger glow
  { type: 'fire_infusion', color: 0xff6644, glowRadius: 6, glowIntensity: 0.5 },
  { type: 'vanish', color: 0x553399, glowRadius: 4, glowIntensity: 0.4 },
  { type: 'fury', color: 0xff4444, glowRadius: 6, glowIntensity: 0.5 },
  { type: 'defy', color: 0xff6644, glowRadius: 5, glowIntensity: 0.4 },
  // Debuffs on players — vulnerability aura
  { type: 'soaked', color: 0x4488bb, glowRadius: 5, glowIntensity: 0.4 },
  { type: 'sunder', color: 0xdd6633, glowRadius: 5, glowIntensity: 0.4 },
  { type: 'weakened', color: 0x886688, glowRadius: 5, glowIntensity: 0.4 },
  // DoTs on players — noticeable aura
  { type: 'poison', color: 0x44cc44, glowRadius: 5, glowIntensity: 0.4 },
  { type: 'bleed', color: 0xcc2222, glowRadius: 5, glowIntensity: 0.4 },
  { type: 'burn', color: 0xff6600, glowRadius: 5, glowIntensity: 0.5 },
  { type: 'shadow_burn', color: 0x7733bb, glowRadius: 5, glowIntensity: 0.5 },
  // Enemy buffs — boss state visuals
  { type: 'arm_up', color: 0xaabbcc, glowRadius: 5, glowIntensity: 0.4 },
  { type: 'regen', color: 0x44cc44, glowRadius: 5, glowIntensity: 0.3 },
  { type: 'str_up', color: 0xff6644, glowRadius: 6, glowIntensity: 0.5 },
  { type: 'overgrowth', color: 0x33aa33, glowRadius: 8, glowIntensity: 0.6 },
  { type: 'shadow_empowered', color: 0x7733bb, glowRadius: 8, glowIntensity: 0.6 },
];

/** Fill opacity for unit ground shadows. Applied as the Ellipse constructor's fillAlpha —
 *  this is the "paint opacity" that persists across the shape's lifetime.
 *
 *  ⚠️ Do NOT use this as a tween target. Fade-in tweens animate GameObject `alpha` (a
 *  multiplier on top of fillAlpha). They must end at 1.0 so the final visible opacity
 *  equals SHADOW_ALPHA, not SHADOW_ALPHA². */
const SHADOW_ALPHA = 0.35;

export class CombatScene extends Phaser.Scene {
  private enemies: CombatantSprite[] = [];
  private enemyShadows: Phaser.GameObjects.Ellipse[] = [];
  private players: CombatantSprite[] = [];
  private playerShadows: Phaser.GameObjects.Ellipse[] = [];
  private background: Phaser.GameObjects.Image | null = null;
  private layout!: GridFormationLayout;
  private sceneData!: CombatSceneData;
  private highlightedIds = new Set<string>();
  private timelineHoveredId: string | null = null;
  private activePlayerId: string | null = null;
  /** Tween that pulses the active turn glow's outerStrength so the highlight
   *  breathes. Stopped + nulled when the active player changes. */
  private activeTurnPulseTween: Phaser.Tweens.Tween | null = null;
  /** Per-sprite idle-breath tweens. Each tween targets a state object (not the
   *  sprite) so attack animations calling killTweensOf(sprite) don't kill them.
   *  The onUpdate skips writing scaleY when another tween is mid-flight on the
   *  sprite, so attack anims always win. */
  private idleBreathTweens: Phaser.Tweens.Tween[] = [];
  private highlightMode: 'single' | 'all' | null = null;
  private hoveredEnemyId: string | null = null;
  private eventUnsubs: Array<() => void> = [];
  private visualLayers = new Map<Phaser.GameObjects.Sprite, Map<VisualLayer, LayerState>>();
  private currentBgKey = 'meadow_bg';
  private mordantOverlay: MordantOverlay | null = null;

  private static readonly LIGHTING_TINTS: Record<string, number> = {
    moonlight: 0x99aadd,
    candlelight: 0xffcc88,
    weavelight: 0xbb88dd,
  };
  private lightingType = 'daylight';

  constructor() {
    super({ key: 'CombatScene' });
  }

  /**
   * Phaser calls init() on every scene start AND restart, before preload/create.
   * Clean up stale state from the previous lifecycle — Phaser does NOT auto-call
   * our shutdown() on restart, so old sprites/handlers accumulate without this.
   */
  init(): void {
    for (const unsub of this.eventUnsubs) unsub();
    this.eventUnsubs = [];
    this.enemies = [];
    this.enemyShadows = [];
    this.players = [];
    this.playerShadows = [];
    this.highlightedIds.clear();
    this.timelineHoveredId = null;
    this.visualLayers.clear();
    this.targetHandlers.clear();
    this.activePlayerId = null;
    this.highlightMode = null;
    this.hoveredEnemyId = null;
    this.background = null;
    if (this.mordantOverlay) {
      this.mordantOverlay.destroy();
      this.mordantOverlay = null;
    }
    // Stop any idle-breath tweens from the previous lifecycle so they don't
    // accumulate across scene restarts. (Targets are state objects, not the
    // sprites, so they're never auto-killed by killTweensOf during attacks.)
    for (const tween of this.idleBreathTweens) tween.stop();
    this.idleBreathTweens = [];
    if (this.activeTurnPulseTween) {
      this.activeTurnPulseTween.stop();
      this.activeTurnPulseTween = null;
    }
    // Remove duplicate resize handler from previous lifecycle
    this.scale.off('resize', this.handleResize, this);

    // Read scene data early so preload() loads the correct background texture.
    // Store it — getSceneData() consumes pendingSceneData, so create() reuses this.
    this.sceneData = this.getSceneData();
    if (this.sceneData.bgKey) {
      this.currentBgKey = this.sceneData.bgKey;
    }
    this.lightingType = this.sceneData.lightingType ?? 'daylight';
  }

  preload(): void {
    const base = import.meta.env.BASE_URL;

    this.load.on('loaderror', (file: { key: string; url: string }) => {
      console.error(`[CombatScene] Failed to load: ${file.key} from ${file.url}`);
    });

    // Drive the combat loading bar in the DOM overlay
    const barEl = document.getElementById('combat-loading-bar');
    if (barEl) {
      this.load.on('progress', (value: number) => {
        barEl.style.width = `${Math.round(value * 100)}%`;
      });
    }

    // Only load sprites that are in the current encounter (not the entire sprite catalog)
    const neededKeys = new Set<string>();
    if (this.sceneData) {
      for (const e of this.sceneData.enemies) neededKeys.add(e.spriteKey);
      for (const p of this.sceneData.players) neededKeys.add(p.spriteKey);
    }
    // Form-transition preload: bosses with mid-fight sprite swaps need their
    // form sprites loaded up front so the transition is instant. Frontend
    // mirror of the backend's `formTransitions`. Add new entries as bosses
    // with form-swap mechanics ship.
    const FORM_TRANSITION_PRELOAD: Record<string, string[]> = {
      velara_mourn: ['velara_steeped_form', 'velara_violet_form'],
    };
    for (const e of this.sceneData?.enemies ?? []) {
      const formKeys = FORM_TRANSITION_PRELOAD[e.spriteKey];
      if (formKeys) for (const k of formKeys) neededKeys.add(k);
    }

    for (const key of neededKeys) {
      const config = ENEMY_SPRITES[key] ?? PLAYER_SPRITES[key];
      if (!config) continue;
      if (!this.textures.exists(config.smKey)) {
        this.load.image(config.smKey, `${base}${config.smPath}`);
      }
      if (config.actionSmKey && config.actionSmPath && !this.textures.exists(config.actionSmKey)) {
        this.load.image(config.actionSmKey, `${base}${config.actionSmPath}`);
      }
    }

    // Preload only the current background; others load on-demand
    const defaultBg = BACKGROUNDS[this.currentBgKey];
    if (defaultBg && !this.textures.exists(defaultBg.key)) {
      this.load.image(defaultBg.key, `${base}${defaultBg.path}`);
    }

    this.createEffectGraphics();
  }

  private createEffectGraphics(): void {
    const hitGraphics = this.make.graphics({ x: 0, y: 0 });
    hitGraphics.fillStyle(0xffffff, 1);
    hitGraphics.fillCircle(16, 16, 16);
    hitGraphics.generateTexture('hit-effect', 32, 32);
    hitGraphics.destroy();
  }

  create(): void {
    // sceneData already populated by init() — no need to re-fetch
    if (!this.sceneData.players.length || !this.sceneData.enemies.length) {
      console.warn('[CombatScene] No scene data provided, waiting for combat start');
      return;
    }

    this.initArena();
  }

  private initArena(): void {
    const bounds = this.measureArenaBounds();
    console.log('[CombatScene] Arena bounds:', JSON.stringify(bounds), 'scale:', this.scale.width, 'x', this.scale.height);
    this.layout = new GridFormationLayout(bounds);

    this.createBackground();
    this.createMordantOverlay();
    this.createCombatants();
    this.setupEventListeners();
    this.setActivePlayer(this.sceneData.activeCombatantId ?? null);

    this.scale.on('resize', this.handleResize, this);

    // NOTE: We do NOT emit positions here because CombatHUD hasn't subscribed
    // yet. The HUD subscribes on COMBAT_SCENE_READY, then emits HUD_INITIALIZED,
    // which triggers handleResize → emitCombatantPositions (see setupEventListeners).
    EventBus.emit('COMBAT_SCENE_READY');
  }

  private getSceneData(): CombatSceneData {
    // Check pending data first — set by initializePhaser before every restart/init
    const pending = getPendingSceneData();
    if (pending?.players?.length && pending?.enemies?.length) {
      return pending;
    }
    // Fallback: scene.restart(data) passes via scene.settings.data
    const data = this.scene.settings.data as Partial<CombatSceneData> | undefined;
    if (data?.players?.length && data?.enemies?.length) {
      return data as CombatSceneData;
    }
    // No data available yet
    console.warn('[CombatScene] No scene data available');
    return { players: [], enemies: [] };
  }

  private createBackground(): void {
    this.drawBackground(this.scale.width, this.scale.height);
  }

  private drawBackground(width: number, height: number): void {
    if (this.background) {
      this.background.destroy();
      this.background = null;
    }
    const bg = BACKGROUNDS[this.currentBgKey];
    if (bg && this.textures.exists(bg.key)) {
      // Cover mode, anchored to bottom (matches layout tuner)
      // Crops from the top (sky) rather than evenly, keeping the ground visible
      const img = this.add.image(width / 2, height, bg.key);
      img.setOrigin(0.5, 1); // anchor at bottom-center
      img.setDepth(-10);
      const scaleX = width / img.width;
      const scaleY = height / img.height;
      img.setScale(Math.max(scaleX, scaleY));
      this.background = img;
      return;
    }
    const graphics = this.add.graphics();
    graphics.setDepth(-10);
    graphics.fillStyle(0x121830, 1);
    graphics.fillRect(0, 0, width, height);
    this.background = graphics as unknown as Phaser.GameObjects.Image;
  }

  /**
   * If this fight is a Mordant, layer the Saturation Field overlay on top of
   * the background — chromatic vignette + drifting motes in the dye color.
   * Combat math is already applied server-side; this is purely visual signal.
   */
  private createMordantOverlay(): void {
    const color = this.sceneData.mordantColor;
    if (!color) return;
    this.mordantOverlay = new MordantOverlay(this, color as MordantColorId);
  }

  // ---------------------------------------------------------------------------
  // Create combatants in dynamic formation
  // ---------------------------------------------------------------------------

  private createCombatants(): void {
    const { players: playerDefs, enemies: enemyDefs } = this.sceneData;

    // --- Enemies ---
    const enemyConfigs = enemyDefs.map(def => ENEMY_SPRITES[def.spriteKey] || ENEMY_SPRITES.skarn);
    const enemyPositions = this.layout.getFormationPositions(
      'enemy',
      enemyDefs.length,
      enemyConfigs.map(c => c.scale),
    );

    enemyDefs.forEach((def, i) => {
      const config = enemyConfigs[i];
      const pos = enemyPositions[i];

      const sprite = this.add.sprite(pos.x, pos.y, config.smKey);
      sprite.setOrigin(0.5, 1.0); // bottom-anchored: pos.y = feet
      sprite.setScale(pos.scale);
      sprite.setDepth(pos.depth);
      sprite.setData('baseDisplayHeight', this.textures.get(config.smKey).getSourceImage().height * pos.scale);
      sprite.setData('baseScale', pos.scale);

      // On landscape, sprites are directly clickable; on portrait, DOM picker handles it
      const isPortrait = window.innerWidth / window.innerHeight < 1;
      if (!isPortrait) {
        sprite.setInteractive({ useHandCursor: true });
        sprite.on('pointerdown', () => this.selectTarget(def.id));
        sprite.on('pointerover', () => this.onEnemyHover(def.id));
        sprite.on('pointerout', () => this.onEnemyHoverEnd(def.id));
      }

      this.enemies.push({ sprite, id: def.id, isEnemy: true, config });

      this.enemyShadows.push(this.createShadowAt(pos, sprite.displayWidth, config));

      this.startIdleBreath(sprite, pos.scale);
      this.applyBaseOutline(sprite);
    });

    // --- Players ---
    // Summoned minions (skeletons, etc.) use sprites from ENEMY_SPRITES, so probe both tables.
    // Minion sprites are tuned for backline enemy use (small); boost them when standing in the
    // strand line so they don't read as babies next to full-size strands.
    const playerIsMinion = playerDefs.map(def => !PLAYER_SPRITES[def.spriteKey]);
    const playerConfigs = playerDefs.map(def =>
      PLAYER_SPRITES[def.spriteKey] || ENEMY_SPRITES[def.spriteKey] || PLAYER_SPRITES.warrior,
    );
    const playerPositions = this.layout.getFormationPositions(
      'player',
      playerDefs.length,
      playerConfigs.map((c, i) => c.scale * (playerIsMinion[i] ? MINION_SCALE_BOOST : 1)),
    );

    playerDefs.forEach((def, i) => {
      const config = playerConfigs[i];
      const pos = playerPositions[i];

      const sprite = this.add.sprite(pos.x, pos.y, config.smKey);
      sprite.setOrigin(0.5, 1.0); // bottom-anchored: pos.y = feet
      sprite.setScale(pos.scale);
      sprite.setDepth(pos.depth);
      sprite.setData('baseDisplayHeight', this.textures.get(config.smKey).getSourceImage().height * pos.scale);
      sprite.setData('baseScale', pos.scale);

      this.players.push({ sprite, id: def.id, isEnemy: false, config, isMinion: playerIsMinion[i] });

      this.playerShadows.push(this.createShadowAt(pos, sprite.displayWidth, config));

      this.startIdleBreath(sprite, pos.scale);
      this.applyBaseOutline(sprite);
    });

    // Apply facing directions based on layout mode
    this.updateSpriteFacing();

    // Apply ambient lighting tint to all sprites
    this.applyAmbientLighting();
  }

  // ---------------------------------------------------------------------------
  // Shadows
  // ---------------------------------------------------------------------------

  private shadowCfg(config: SpriteConfig) {
    const o = resolveShadowOffsets(config);
    return { scale: config.shadowScale, offsetX: o.shadowOffsetX + o.centerOffsetX, offsetY: o.shadowOffsetY };
  }

  private createShadowAt(
    unitPos: SlotWorldPosition,
    displayWidth: number,
    config: SpriteConfig,
  ): Phaser.GameObjects.Ellipse {
    const sp = this.layout.getShadowPosition(unitPos, displayWidth, this.shadowCfg(config));
    const shadow = this.add.ellipse(sp.x, sp.y, sp.width, sp.height, 0x000000, SHADOW_ALPHA);
    shadow.setDepth(sp.depth);
    return shadow;
  }

  private updateShadow(
    shadow: Phaser.GameObjects.Ellipse,
    unitPos: SlotWorldPosition,
    displayWidth: number,
    config: SpriteConfig,
  ): void {
    const sp = this.layout.getShadowPosition(unitPos, displayWidth, this.shadowCfg(config));
    shadow.setPosition(sp.x, sp.y);
    shadow.setSize(sp.width, sp.height);
    shadow.setDepth(sp.depth);
  }

  // ---------------------------------------------------------------------------
  // Emit combatant screen positions for DOM floating HP bars
  // ---------------------------------------------------------------------------

  private emitCombatantPositions(): void {
    const positions: CombatantPosition[] = [];

    for (const enemy of this.enemies) {
      if (!enemy.sprite.visible) continue;
      const cx = enemy.config ? resolveCenterOffsetX(enemy.config) : 0;
      positions.push({
        id: enemy.id,
        x: enemy.sprite.x + cx * enemy.sprite.displayWidth,
        y: enemy.sprite.y,
        displayHeight: enemy.sprite.displayHeight,
        isEnemy: true,
      });
    }

    for (const player of this.players) {
      if (!player.sprite.visible) continue;
      const cx = player.config ? resolveCenterOffsetX(player.config) : 0;
      positions.push({
        id: player.id,
        x: player.sprite.x + cx * player.sprite.displayWidth,
        y: player.sprite.y,
        displayHeight: player.sprite.displayHeight,
        isEnemy: false,
      });
    }

    const payload: CombatantPositionsPayload = { positions };
    EventBus.emit('COMBATANT_POSITIONS_UPDATED', payload);
  }

  // ---------------------------------------------------------------------------
  // Arena measurement
  // ---------------------------------------------------------------------------

  private measureArenaBounds(): ArenaBounds {
    const topBar = document.getElementById('combat-top-bar');
    const actionPanel = document.getElementById('action-panel');

    const topRect = topBar?.getBoundingClientRect();
    const panelRect = actionPanel?.getBoundingClientRect();

    // On first load the DOM may not have laid out yet (all rects zero).
    // Fall back to Phaser scale dimensions so sprites are placed reasonably;
    // HUD_INITIALIZED → handleResize will correct once DOM is ready.
    const domReady = (topRect?.height ?? 0) > 0 && (panelRect?.height ?? 0) > 0;

    const arenaTop = domReady ? topRect!.bottom : this.scale.height * 0.1;
    const arenaBottom = domReady ? panelRect!.top : this.scale.height * 0.9;

    return {
      top: arenaTop,
      bottom: Math.max(arenaBottom, arenaTop + 100),
      left: 0,
      right: this.scale.width,
    };
  }

  // ---------------------------------------------------------------------------
  // Resize
  // ---------------------------------------------------------------------------

  private handleResize(): void {
    const width = this.scale.width;
    const height = this.scale.height;

    this.drawBackground(width, height);
    this.layout.updateArenaBounds(this.measureArenaBounds());

    // Enemies
    if (this.enemies.length > 0) {
      const enemyPositions = this.layout.getFormationPositions(
        'enemy',
        this.enemies.length,
        this.enemies.map(e => e.config.scale),
      );
      this.enemies.forEach((enemy, i) => {
        const pos = enemyPositions[i];
        enemy.sprite.setPosition(pos.x, pos.y);
        enemy.sprite.setScale(pos.scale);
        enemy.sprite.setDepth(pos.depth);
        enemy.sprite.setData('baseDisplayHeight', this.textures.get(enemy.config.smKey).getSourceImage().height * pos.scale);
        enemy.sprite.setData('baseScale', pos.scale);
        enemy.sprite.setData('breathBaseScale', pos.scale);
        this.updateShadow(this.enemyShadows[i], pos, enemy.sprite.displayWidth, enemy.config);
      });
    }

    // Players
    if (this.players.length > 0) {
      const playerPositions = this.layout.getFormationPositions(
        'player',
        this.players.length,
        this.players.map(p => p.config.scale * (p.isMinion ? MINION_SCALE_BOOST : 1)),
      );
      this.players.forEach((player, i) => {
        const pos = playerPositions[i];
        player.sprite.setPosition(pos.x, pos.y);
        player.sprite.setScale(pos.scale);
        player.sprite.setDepth(pos.depth);
        player.sprite.setData('baseDisplayHeight', this.textures.get(player.config.smKey).getSourceImage().height * pos.scale);
        player.sprite.setData('baseScale', pos.scale);
        player.sprite.setData('breathBaseScale', pos.scale);
        this.updateShadow(this.playerShadows[i], pos, player.sprite.displayWidth, player.config);
      });
    }

    // Update facing for new aspect ratio
    this.updateSpriteFacing();

    // Reapply PostFX (glow effects) — Phaser's internal resize destroys
    // RenderTargets, so we need to recreate them from our visual layer state.
    for (const enemy of this.enemies) this.resolveVisual(enemy.sprite);
    for (const player of this.players) this.resolveVisual(player.sprite);

    // Re-emit positions so floating HP bars reposition
    this.emitCombatantPositions();
  }

  // ---------------------------------------------------------------------------
  // Sprite facing based on layout mode
  // ---------------------------------------------------------------------------

  private updateSpriteFacing(): void {
    // Enemies face LEFT toward players -> flipX(true)
    for (const enemy of this.enemies) {
      enemy.sprite.setFlipX(!enemy.config.flipFacing);
    }

    // Players face RIGHT toward enemies -> flipX(false)
    for (const player of this.players) {
      player.sprite.setFlipX(!!player.config.flipFacing);
    }
  }

  // ---------------------------------------------------------------------------
  // Event listeners
  // ---------------------------------------------------------------------------

  private setupEventListeners(): void {
    this.eventUnsubs.push(
      // Ability animation events (AnimationDef-driven)
      EventBus.on(Events.COMBAT_ABILITY_USED, (payload: unknown) => {
        const { sourceId, animation, isCharging } = payload as {
          sourceId?: string; animation: AnimationDef; isCharging?: boolean;
        };
        this.playCastAnimation(sourceId, animation, isCharging);
      }),
      EventBus.on(Events.COMBAT_HIT, (payload: unknown) => {
        const { targetId, animation, side } = payload as {
          targetId: string; animation: AnimationDef; side: string;
        };
        this.playHitAnimation(targetId, animation, side);
      }),

      EventBus.on('COMBAT_DEFEND', () => this.playDefendAnimation()),
      EventBus.on('COMBAT_ENEMY_DEFEATED', (targetId?: unknown) => this.playDefeatedAnimation(targetId as string | undefined)),

      // Break threshold trip — fire the sprite-side shatter effect alongside the
      // DOM bar flourish and the BROKEN! floating text. Only the broken:true
      // BREAK_UPDATE payload triggers; non-trigger fill updates are silent.
      EventBus.on(Events.BREAK_UPDATE, (payload: unknown) => {
        const { id, broken } = payload as { id: string; broken?: boolean };
        if (!broken) return;
        const combatant = this.findCombatant(id);
        if (!combatant) return;
        playPreset(this, combatant.sprite, 'break_shatter', 0xd987ff);
      }),

      // Charge telegraph: enemy is winding up an attack at this combatant.
      EventBus.on(Events.COMBAT_CAST_TARGET_ENTER, (payload: unknown) => {
        const { targetId, tint } = payload as { sourceId: string; targetId: string; tint?: number; side: 'enemy' | 'player' };
        this.applyCastTarget(targetId, tint ?? 0xff4444);
      }),
      EventBus.on(Events.COMBAT_CAST_TARGET_EXIT, (payload: unknown) => {
        const { targetId } = payload as { sourceId: string; targetId: string };
        this.clearCastTarget(targetId);
      }),
      EventBus.on(Events.COMBAT_ENEMY_SUMMONED, (payload: unknown) => {
        const data = payload as { id: string; spriteKey: string; name: string; hp: number; maxHp: number; ownerId?: string; defensiveTypes?: string[] };
        this.addSummonedEnemy(data);
      }),
      EventBus.on(Events.COMBAT_ALLY_SUMMONED, (payload: unknown) => {
        const data = payload as { id: string; spriteKey: string; name: string; hp: number; maxHp: number; ownerId?: string; defensiveTypes?: string[] };
        this.addSummonedAlly(data);
      }),
      EventBus.on(Events.COMBAT_ENEMY_TRANSFORMED, (payload: unknown) => {
        const data = payload as { id: string; spriteKey: string; name?: string; transformAnimationKey?: string };
        this.transformEnemy(data);
      }),

      // Targeting highlights (DOM -> Phaser)
      EventBus.on('COMBAT_HIGHLIGHT_TARGETS', (payload: unknown) => {
        const { ids, mode, side } = payload as { ids: string[]; mode: 'single' | 'all'; side?: 'enemy' | 'player' };
        this.highlightTargets(ids, mode, side ?? 'enemy');
      }),
      EventBus.on('COMBAT_CLEAR_HIGHLIGHTS', () => this.clearHighlights()),

      // Clean up stale targeting highlights after playback completes
      EventBus.on(Events.COMBAT_PLAYBACK_COMPLETE, () => {
        if (this.highlightedIds.size > 0) this.clearHighlights();
      }),

      // Re-emit positions once the DOM HUD has subscribed (fixes timing race)
      // Use setTimeout so DOM (ability buttons) has rendered before remeasuring arena
      EventBus.on('HUD_INITIALIZED', () => {
        setTimeout(() => this.handleResize(), 0);
      }),

      // Timeline hover highlights battlefield sprite
      EventBus.on('TIMELINE_HOVER', (id: unknown) => {
        this.highlightTimelineUnit(id as string | null);
      }),

      // Active turn changed (server-driven)
      EventBus.on(Events.ACTIVE_TURN_CHANGED, (id: unknown) => this.setActivePlayer(id as string)),

      // Room transition — keep players, swap enemies
      EventBus.on(Events.COMBAT_ROOM_TRANSITION, (data: unknown) => {
        this.transitionRoom(data as CombatSceneData);
      }),

      // Crawl celebration (golden particles + camera effects)
      EventBus.on(Events.COMBAT_CRAWL_CELEBRATE, () => {
        this.playCrawlCelebration();
      }),

      // Hide all combat sprites for debrief — background stays visible
      EventBus.on('COMBAT_HIDE_SPRITES', () => {
        for (const c of this.players) { c.sprite.setVisible(false); }
        for (const c of this.enemies) { c.sprite.setVisible(false); }
        for (const s of this.playerShadows) { s.setVisible(false); }
        for (const s of this.enemyShadows) { s.setVisible(false); }
      }),

      // Persistent status effect visuals (glow on buff/debuff)
      EventBus.on(Events.STATUS_EFFECTS_UPDATE, (payload: unknown) => {
        const { id, statusEffects } = payload as {
          id: string;
          statusEffects: Array<{ type: string; category: string }>;
        };
        this.updateStatusEffectVisual(id, statusEffects);
      }),

      // Debug: swap lighting type
      EventBus.on(Events.DEBUG_CHANGE_LIGHTING, (type: unknown) => {
        this.lightingType = type as string;
        // Clear existing lighting layers, then reapply
        for (const c of [...this.players, ...this.enemies]) {
          this.clearLayer(c.sprite, 'lighting');
        }
        this.applyAmbientLighting();
      }),

      // Debug: swap background (lazy-load texture if needed)
      EventBus.on(Events.DEBUG_CHANGE_BACKGROUND, (key: unknown) => {
        const bgKey = key as string;
        const bg = BACKGROUNDS[bgKey];
        if (!bg) return;
        this.currentBgKey = bgKey;
        if (this.textures.exists(bg.key)) {
          this.drawBackground(this.scale.width, this.scale.height);
        } else {
          const base = import.meta.env.BASE_URL;
          this.load.image(bg.key, `${base}${bg.path}`);
          this.load.once('complete', () => {
            this.drawBackground(this.scale.width, this.scale.height);
          });
          this.load.start();
        }
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // Find combatant sprite by id (searches both sides)
  // ---------------------------------------------------------------------------

  private findCombatant(id: string): CombatantSprite | null {
    return this.enemies.find(e => e.id === id)
      ?? this.players.find(p => p.id === id)
      ?? null;
  }

  // ---------------------------------------------------------------------------
  // Targeting
  // ---------------------------------------------------------------------------

  private selectTarget(enemyId: string): void {
    EventBus.emit('COMBAT_TARGET_SELECTED', enemyId);
  }

  private highlightTargets(targetIds: string[], mode: 'single' | 'all', side: 'enemy' | 'player'): void {
    this.clearHighlights();
    this.highlightMode = mode;

    let color: number;
    if (side === 'player') {
      color = 0x6688ff; // blue = targetable (same as enemy single)
    } else {
      color = mode === 'all' ? 0xff4444 : 0x6688ff; // red for AoE, blue for single
    }

    const pool = side === 'enemy' ? this.enemies : this.players;
    for (const tid of targetIds) {
      const entry = pool.find(e => e.id === tid);
      if (!entry) continue;

      this.setLayer(entry.sprite, 'targeting', color, { glowOnly: true });
      this.highlightedIds.add(tid);

      // Make sprites interactive for targeting (skip on portrait where DOM picker handles it)
      const isPortrait = window.innerWidth / window.innerHeight < 1;
      if (!isPortrait) {
        if (side === 'player') {
          entry.sprite.setInteractive({ useHandCursor: true });
        }
        entry.sprite.on('pointerdown', this.getHandler(`target_${tid}`, () => this.selectTarget(tid)));
        entry.sprite.on('pointerover', this.getHandler(`hover_${tid}`, () => this.onAllyHover(tid)));
        entry.sprite.on('pointerout', this.getHandler(`hoverend_${tid}`, () => this.onAllyHoverEnd(tid)));
      }
    }
  }

  /** Returns a named handler so we can remove it on clearHighlights. */
  private targetHandlers = new Map<string, () => void>();

  private getHandler(key: string, callback: () => void): () => void {
    if (!this.targetHandlers.has(key)) {
      this.targetHandlers.set(key, callback);
    }
    return this.targetHandlers.get(key)!;
  }

  private clearHighlights(): void {
    this.highlightMode = null;
    this.hoveredEnemyId = null;

    for (const tid of this.highlightedIds) {
      const enemyEntry = this.enemies.find(e => e.id === tid);
      if (enemyEntry) this.clearLayer(enemyEntry.sprite, 'targeting');

      const playerEntry = this.players.find(p => p.id === tid);
      if (playerEntry) {
        this.clearLayer(playerEntry.sprite, 'targeting');
        // Remove temporary interactive handlers from player sprites
        const clickHandler = this.targetHandlers.get(`target_${tid}`);
        if (clickHandler) playerEntry.sprite.off('pointerdown', clickHandler);
        const hoverHandler = this.targetHandlers.get(`hover_${tid}`);
        if (hoverHandler) playerEntry.sprite.off('pointerover', hoverHandler);
        const hoverEndHandler = this.targetHandlers.get(`hoverend_${tid}`);
        if (hoverEndHandler) playerEntry.sprite.off('pointerout', hoverEndHandler);
        playerEntry.sprite.disableInteractive();
      }
    }
    this.highlightedIds.clear();
    this.targetHandlers.clear();
  }

  private onEnemyHover(enemyId: string): void {
    if (this.highlightMode !== 'single') return;
    if (!this.highlightedIds.has(enemyId)) return;

    this.hoveredEnemyId = enemyId;
    const entry = this.enemies.find(e => e.id === enemyId);
    // Hover = red to indicate "will attack this one"
    if (entry) this.setLayer(entry.sprite, 'targeting', 0xff4444, { glowOnly: true });
    EventBus.emit('COMBAT_TARGET_HOVERED', enemyId);
  }

  private onEnemyHoverEnd(enemyId: string): void {
    if (this.highlightMode !== 'single') return;
    if (!this.highlightedIds.has(enemyId)) return;
    if (this.hoveredEnemyId !== enemyId) return;

    this.hoveredEnemyId = null;
    const entry = this.enemies.find(e => e.id === enemyId);
    // Back to blue (neutral highlight)
    if (entry) this.setLayer(entry.sprite, 'targeting', 0x6688ff, { glowOnly: true });
    EventBus.emit('COMBAT_TARGET_HOVERED', null);
  }

  private onAllyHover(allyId: string): void {
    if (this.highlightMode !== 'single') return;
    if (!this.highlightedIds.has(allyId)) return;
    const entry = this.players.find(p => p.id === allyId);
    if (entry) this.setLayer(entry.sprite, 'targeting', 0x00ff88, { glowOnly: true }); // green = will heal
    EventBus.emit('COMBAT_TARGET_HOVERED', allyId);
  }

  private onAllyHoverEnd(allyId: string): void {
    if (this.highlightMode !== 'single') return;
    if (!this.highlightedIds.has(allyId)) return;
    const entry = this.players.find(p => p.id === allyId);
    if (entry) this.setLayer(entry.sprite, 'targeting', 0x6688ff, { glowOnly: true }); // back to blue
    EventBus.emit('COMBAT_TARGET_HOVERED', null);
  }

  // ---------------------------------------------------------------------------
  // Visual layer system
  // ---------------------------------------------------------------------------

  /** Update the persistent glow on a combatant based on their active status effects.
   *  Picks the highest-priority visual from STATUS_EFFECT_VISUALS that matches an
   *  active effect, then sets or clears the 'statusEffect' visual layer. */
  private updateStatusEffectVisual(
    combatantId: string,
    statusEffects: Array<{ type: string; category: string }>,
  ): void {
    const entry = this.findCombatant(combatantId);
    if (!entry) return;

    // Walk the config in order — last match wins (highest priority)
    let winning: (typeof STATUS_EFFECT_VISUALS)[number] | null = null;
    const activeTypes = new Set(statusEffects.map(e => e.type));
    for (const cfg of STATUS_EFFECT_VISUALS) {
      if (activeTypes.has(cfg.type)) winning = cfg;
    }

    if (winning) {
      this.setLayer(entry.sprite, 'statusEffect', winning.color, {
        glowOnly: true,
        glowRadius: winning.glowRadius,
        glowIntensity: winning.glowIntensity,
      });
    } else {
      this.clearLayer(entry.sprite, 'statusEffect');
    }
  }

  private applyAmbientLighting(): void {
    const tint = CombatScene.LIGHTING_TINTS[this.lightingType];
    if (!tint) return; // daylight = no tint
    for (const c of [...this.players, ...this.enemies]) {
      this.setLayer(c.sprite, 'lighting', tint, { noGlow: true });
    }
  }

  private setLayer(
    sprite: Phaser.GameObjects.Sprite,
    layer: VisualLayer,
    color: number,
    opts?: { glowOnly?: boolean; glowRadius?: number; glowIntensity?: number; noGlow?: boolean },
  ): void {
    if (!this.visualLayers.has(sprite)) {
      this.visualLayers.set(sprite, new Map());
    }
    this.visualLayers.get(sprite)!.set(layer, {
      color,
      glowOnly: opts?.glowOnly,
      glowRadius: opts?.glowRadius,
      glowIntensity: opts?.glowIntensity,
      noGlow: opts?.noGlow,
    });
    this.resolveVisual(sprite);
  }

  /** Start a barely-perceptible scaleY breathing loop on a combat sprite so it
   *  feels alive between attacks. The tween targets a state object (not the
   *  sprite) so the existing attack animations' killTweensOf(sprite) calls
   *  don't kill it. The onUpdate yields to any in-flight attack tweens by
   *  checking getTweensOf(sprite) — when an attack is animating, the breath
   *  pauses; when the attack ends, breathing resumes seamlessly. */
  private startIdleBreath(sprite: Phaser.GameObjects.Sprite, baseScale: number): void {
    // Store baseScale on the sprite so handleResize can update it.
    // The breathing tween reads this each frame instead of a stale closure.
    sprite.setData('breathBaseScale', baseScale);
    const state = { phase: 0 };
    // Random per-sprite delay so combatants don't all breathe in lockstep —
    // up to one full cycle (3200ms) of offset means the party + enemy line
    // looks like distinct living characters, not a chorus line.
    const stagger = Math.random() * 3200;
    const tween = this.tweens.add({
      targets: state,
      phase: { from: 0, to: 1 },
      duration: 3200,
      delay: stagger,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        if (!sprite.active) return;
        // Yield to any other tweens currently targeting this sprite (attack
        // animations, hit reactions, channel pulses, etc.). When they finish,
        // we resume writing scaleY on the next frame.
        const otherTweens = this.tweens.getTweensOf(sprite);
        if (otherTweens.length > 0) return;
        // Tiny scale-Y breath: 1.0 → 1.018 of base. Bottom-anchored origin
        // means the figure breathes upward (head bobs slightly, feet planted).
        const bs = sprite.getData('breathBaseScale') as number;
        sprite.scaleY = bs * (1 + state.phase * 0.018);
      },
    });
    this.idleBreathTweens.push(tween);
  }

  /** Find the active Glow PostFX on a sprite, if one is currently applied.
   *  Used to tween its outerStrength for the active turn pulse. */
  private getSpriteGlow(sprite: Phaser.GameObjects.Sprite): Phaser.FX.Glow | null {
    const list = sprite.postFX?.list ?? [];
    for (const fx of list) {
      if ((fx as Phaser.FX.Glow).outerStrength !== undefined) {
        return fx as Phaser.FX.Glow;
      }
    }
    return null;
  }

  private clearLayer(sprite: Phaser.GameObjects.Sprite, layer: VisualLayer): void {
    const layers = this.visualLayers.get(sprite);
    if (!layers) return;
    layers.delete(layer);
    this.resolveVisual(sprite);
  }

  private clearAllLayers(sprite: Phaser.GameObjects.Sprite): void {
    this.visualLayers.delete(sprite);
    sprite.clearTint();
    if (sprite.postFX) sprite.postFX.clear();
  }

  /** Apply the always-on black silhouette ring to a sprite. Runs through
   *  our custom PreFX shader (OutlinePipeline) — binary hit-test, no blur.
   *  1-texel thickness = crisp pixel-perfect outline. */
  private applyBaseOutline(sprite: Phaser.GameObjects.Sprite): void {
    OutlinePipeline.applyBase(sprite, 0x000000, 1);
  }

  /** UI layers (activeTurn, targeting) route through the OutlinePipeline's
   *  second ring — same shader, drawn outside the base ring, hard-edged.
   *  Gameplay layers (statusEffect) still use postFX.addGlow for a soft
   *  halo that reads as aura, not structural outline. */
  private static readonly OUTLINE_LAYERS: ReadonlySet<VisualLayer> = new Set(['activeTurn', 'targeting']);

  private resolveVisual(sprite: Phaser.GameObjects.Sprite): void {
    const layers = this.visualLayers.get(sprite);
    sprite.clearTint();
    // Reset postFX glows; the preFX outline shader lives on a separate
    // channel (the main pipeline) and is not affected by postFX.clear().
    if (sprite.postFX) sprite.postFX.clear();
    OutlinePipeline.clearHighlight(sprite);

    // Highest-priority active layer wins
    let winning: LayerState | null = null;
    let winningLayer: VisualLayer | null = null;
    if (layers && layers.size > 0) {
      for (const layerName of LAYER_PRIORITY) {
        const state = layers.get(layerName);
        if (state) { winning = state; winningLayer = layerName; }
      }
    }

    if (winning !== null && !winning.glowOnly) {
      sprite.setTint(winning.color);
    }

    if (!winning || winning.noGlow) return;

    const useOutline = winningLayer !== null && CombatScene.OUTLINE_LAYERS.has(winningLayer);

    if (useOutline) {
      // --- Hard-edge ring for UI indicators (active turn, targeting) ---
      OutlinePipeline.setHighlight(sprite, winning.color, winning.glowRadius ?? 2);
    } else if (sprite.postFX) {
      // --- Soft PostFX glow for status effects ---
      try {
        sprite.postFX.addGlow(
          winning.color,
          winning.glowRadius ?? 6,
          0,
          false,
          winning.glowIntensity ?? 1,
          4,
        );
      } catch {
        // PostFX framebuffer can be corrupted after resize/context loss — skip glow
      }
    }
  }

  /** Re-apply the visual layer state after an external tint/postFX change (e.g. animation flash). */
  public restoreVisual(sprite: Phaser.GameObjects.Sprite): void {
    this.resolveVisual(sprite);
  }

  // ---------------------------------------------------------------------------
  // Active player indicator
  // ---------------------------------------------------------------------------

  private setActivePlayer(playerId: string | null): void {
    const isNewPlayer = playerId ? this.players.some(p => p.id === playerId) : false;

    // Stop any existing pulse tween before changing active players.
    if (this.activeTurnPulseTween) {
      this.activeTurnPulseTween.stop();
      this.activeTurnPulseTween = null;
    }

    // Always clear previous player's glow when the active turn changes
    if (this.activePlayerId && this.activePlayerId !== playerId) {
      const prev = this.players.find(p => p.id === this.activePlayerId);
      if (prev) this.clearLayer(prev.sprite, 'activeTurn');
    }

    if (isNewPlayer) {
      this.activePlayerId = playerId;
      const entry = this.players.find(p => p.id === playerId);
      if (entry) {
        this.setLayer(entry.sprite, 'activeTurn', 0xffffff, { glowOnly: true, glowRadius: 2 });
      }
    } else {
      this.activePlayerId = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Preset-driven animations
  // ---------------------------------------------------------------------------

  /** Called on COMBAT_ABILITY_USED — plays the cast phase on the attacker */
  private playCastAnimation(sourceId: string | undefined, anim: AnimationDef, isCharging?: boolean): void {
    if (!sourceId) return;

    const attacker = this.findCombatant(sourceId);
    if (!attacker) return;

    // Texture swap to action pose if the sprite has one
    // Skip texture swap during charging — the action sprite should only appear when the ability fires
    if (attacker.config.actionSmKey && !isCharging) {
      const idleKey = attacker.config.smKey;
      const actionKey = attacker.config.actionSmKey;
      // Use the stored pixel scale — keeps the character body the same size.
      // Since origin is bottom-anchored (0.5, 1.0), taller action sprites
      // (e.g. hammer overhead) extend upward instead of shrinking the body.
      const scale = (attacker.sprite.getData('baseScale') as number) ?? attacker.sprite.scaleY;
      // Kill any in-flight scale tweens to prevent them from stomping our swap
      this.tweens.killTweensOf(attacker.sprite);
      attacker.sprite.setTexture(actionKey);
      attacker.sprite.setScale(scale);
      // Update breathBaseScale so the idle-breath tween uses the correct scale
      attacker.sprite.setData('breathBaseScale', scale);
      // Brief screen flash only when the animation requests it (e.g. big attacks)
      if (anim.textureSwap) {
        const r = (anim.tint >> 16) & 0xff;
        const g = (anim.tint >> 8) & 0xff;
        const b = anim.tint & 0xff;
        this.cameras.main.flash(100, r, g, b, false);
      }
      this.time.delayedCall(anim.actionHoldMs ?? 450, () => {
        if (attacker.sprite.active) {
          this.tweens.killTweensOf(attacker.sprite);
          attacker.sprite.setTexture(idleKey);
          attacker.sprite.setScale(scale);
          // Restore breathBaseScale to idle scale
          attacker.sprite.setData('breathBaseScale', scale);
        }
      });
    }

    // Play cast movement (lunge, step, channel, etc.)
    playCastPreset(this, attacker.sprite, anim.castMotion, attacker.isEnemy);

    // Caster glow (default on unless explicitly disabled)
    if (anim.casterGlow !== false) {
      flashCasterGlow(this, attacker.sprite, anim.tint);
    }
  }

  /** Called on COMBAT_HIT — plays the hit effect on the target sprite */
  private playHitAnimation(targetId: string, anim: AnimationDef, side: string): void {
    const combatant = this.findCombatant(targetId);
    if (!combatant) return;

    playPreset(this, combatant.sprite, anim.hitEffect, anim.tint);

    // Camera shake from animation def, or default for player-side hits
    if (anim.screenShake) {
      this.cameras.main.shake(100, anim.screenShake);
    } else if (side === 'player') {
      this.cameras.main.shake(100, 0.005);
    }
  }

  /** Charged-cast telegraph: play the corner-bracket lock once on the victim.
   *  No persistent glow — re-fires per cast_tick for multi-turn casts so the
   *  player gets a fresh beat each turn instead of a constant indicator. */
  private applyCastTarget(targetId: string, tint: number): void {
    const combatant = this.findCombatant(targetId);
    if (!combatant) return;
    playPreset(this, combatant.sprite, 'target_lock', tint);
  }

  private clearCastTarget(_targetId: string): void {
    // No persistent state to tear down — target_lock is fire-and-forget. The
    // EXIT event still fires from EventPlaybackManager so we keep this hook
    // wired for symmetry / future state additions.
  }

  /** Dynamically add a summoned enemy mid-combat — inserts adjacent to the summoner,
   *  alternating right/left based on how many summons the host already has on the
   *  field. Keeps multi-mote bursts (e.g. Rift's Rupture) balanced around the boss
   *  instead of stacking up on one side. */
  /** Mid-fight enemy form swap — used by HP-threshold boss form transitions
   *  (e.g. Velara Mourn's Recanted → Steeped → Violet ladder). Loads the new
   *  sprite lazily if not cached, then setTexture + scale-tween + flash effect.
   *  Preserves slot position; the same combatant entry stays — only its visual
   *  identity changes. */
  private transformEnemy(data: { id: string; spriteKey: string; name?: string; transformAnimationKey?: string }): void {
    const enemy = this.enemies.find(e => e.id === data.id);
    if (!enemy) return;

    const newConfig = ENEMY_SPRITES[data.spriteKey] ?? PLAYER_SPRITES[data.spriteKey];
    if (!newConfig) return;

    const applyTexture = () => {
      // Update cached config so subsequent layout / shadow / outline reads the new size
      enemy.config = newConfig;

      // Swap texture, then tween to the new scale (different forms may have
      // different recommended scales).
      const enemyIdx = this.enemies.indexOf(enemy);
      const allConfigs = this.enemies.map(e => e.config);
      const positions = this.layout.getFormationPositions('enemy', this.enemies.length, allConfigs.map(c => c.scale));
      const targetPos = positions[enemyIdx];

      enemy.sprite.setTexture(newConfig.smKey);
      this.tweens.add({
        targets: enemy.sprite,
        scale: targetPos.scale,
        duration: 350,
        ease: 'Power2',
      });
      enemy.sprite.setData('breathBaseScale', targetPos.scale);

      // Re-anchor shadow to the new sprite size
      if (this.enemyShadows[enemyIdx]) {
        this.updateShadow(this.enemyShadows[enemyIdx], targetPos, enemy.sprite.displayWidth, newConfig);
      }

      // Dispatch to a transform preset by key (data-driven from FormTransition.transformAnimationKey).
      // Falls back to the generic white flash when no key is wired.
      playTransformPreset(this, enemy.sprite, targetPos.scale, data.transformAnimationKey);

      this.applyBaseOutline(enemy.sprite);
    };

    if (this.textures.exists(newConfig.smKey)) {
      applyTexture();
    } else {
      // Lazy load — sprite wasn't preloaded (form transition was added after
      // initial encounter setup, or this is the first time this form is seen).
      const base = '/sprites/processed/';
      this.load.image(newConfig.smKey, `${base}${newConfig.smPath}`);
      this.load.once('complete', applyTexture);
      this.load.start();
    }
  }

  private addSummonedEnemy(data: { id: string; spriteKey: string; name: string; hp: number; maxHp: number; ownerId?: string; defensiveTypes?: string[] }): void {
    const config = ENEMY_SPRITES[data.spriteKey] || ENEMY_SPRITES.skarn;

    const hostIdx = data.ownerId
      ? this.enemies.findIndex(e => e.id === data.ownerId)
      : -1;

    let insertIdx: number;
    if (hostIdx < 0) {
      insertIdx = this.sceneData.enemies.length;
    } else {
      // Alternate: even-indexed summon goes right of host, odd goes left.
      const existingSummons = this.enemies.filter(e => e.isSummoned).length;
      const placeRight = existingSummons % 2 === 0;
      insertIdx = placeRight ? hostIdx + 1 : hostIdx;
    }

    // Insert at the adjacent slot
    this.sceneData.enemies.splice(insertIdx, 0, {
      id: data.id,
      spriteKey: data.spriteKey,
      name: data.name,
      hp: data.hp,
      maxHp: data.maxHp,
    });

    // Recalculate all enemy formation positions for the new count
    const enemyConfigs = [
      ...this.enemies.slice(0, insertIdx).map(e => e.config),
      config,
      ...this.enemies.slice(insertIdx).map(e => e.config),
    ];
    const enemyPositions = this.layout.getFormationPositions(
      'enemy',
      this.sceneData.enemies.length,
      enemyConfigs.map(c => c.scale),
    );

    // Tween existing enemies to their new positions (skipping the insertion slot)
    this.enemies.forEach((enemy, i) => {
      const newSlot = i < insertIdx ? i : i + 1;
      const pos = enemyPositions[newSlot];
      this.tweens.add({
        targets: enemy.sprite,
        x: pos.x,
        y: pos.y,
        duration: 300,
        ease: 'Power2',
      });
      enemy.sprite.setScale(pos.scale);
      enemy.sprite.setDepth(pos.depth);
      enemy.sprite.setData('breathBaseScale', pos.scale);
      this.updateShadow(this.enemyShadows[i], pos, enemy.sprite.displayWidth, enemy.config);
    });

    // Create new sprite at the adjacent slot with alpha 0
    const newPos = enemyPositions[insertIdx];

    const sprite = this.add.sprite(newPos.x, newPos.y, config.smKey);
    sprite.setOrigin(0.5, 1.0);
    sprite.setScale(newPos.scale);
    sprite.setDepth(newPos.depth);
    // Sprite is visible immediately — the entrance preset (void_emerge / summon_rise)
    // sells the materialization via portal, flash, glow, and scale. An old alpha 0→1
    // fade-in fought the preset's tint/glow stack and left the mote invisible.

    const isPortrait = window.innerWidth / window.innerHeight < 1;
    if (!isPortrait) {
      sprite.setInteractive({ useHandCursor: true });
      sprite.on('pointerdown', () => this.selectTarget(data.id));
      sprite.on('pointerover', () => this.onEnemyHover(data.id));
      sprite.on('pointerout', () => this.onEnemyHoverEnd(data.id));
    }

    this.enemies.splice(insertIdx, 0, { sprite, id: data.id, isEnemy: true, config, isSummoned: true });

    this.startIdleBreath(sprite, newPos.scale);
    this.applyBaseOutline(sprite);

    // Shadow still fades in — softens the "pop-in" footprint while the preset plays above.
    const shadow = this.createShadowAt(newPos, sprite.displayWidth, config);
    shadow.setAlpha(0);
    this.enemyShadows.splice(insertIdx, 0, shadow);

    // Apply facing and ambient lighting
    sprite.setFlipX(true);
    const tint = CombatScene.LIGHTING_TINTS[this.lightingType];
    if (tint) {
      this.setLayer(sprite, 'lighting', tint, { noGlow: true });
    }

    this.tweens.add({
      targets: shadow,
      alpha: 1, // GameObject alpha multiplier; fillAlpha=SHADOW_ALPHA controls final darkness
      duration: 400,
      ease: 'Power2',
      onComplete: () => {
        this.emitCombatantPositions();
      },
    });

    // Play an entrance preset so the spawn reads as a real beat. Picks a void
    // portal for dark-typed summons (Rift's motes), gentle ground-rise otherwise.
    this.playSummonEntrance(sprite, data.defensiveTypes);
  }

  /** Pick and fire an entrance preset on a freshly-spawned summon sprite. The
   *  fade-in tween still runs in parallel; the preset adds drama (portal swirl,
   *  particle burst, glow ramp) keyed off the host's primary defensive type. */
  private playSummonEntrance(sprite: Phaser.GameObjects.Sprite, defensiveTypes?: string[]): void {
    const primary = defensiveTypes?.[0]?.toLowerCase();
    const presetName = primary === 'dark' ? 'void_emerge' : 'summon_rise';
    const tint = primary ? this.tintForElement(primary) : 0xc0c0c0;
    playPreset(this, sprite, presetName, tint);
  }

  /** Convert an element string ("dark", "fire", ...) into a hex tint usable by
   *  Phaser tweens. Falls back to neutral grey for unknown elements. */
  private tintForElement(element: string): number {
    const hex = elementColor(element);
    return parseInt(hex.replace('#', ''), 16);
  }

  /** Dynamically add a player-summoned minion mid-combat (Conjuration/Manifestation skills).
   *  Inserts the minion adjacent (immediately to the right) of its summoning host so the
   *  visual ownership relationship is obvious. Other strands shift to make room.
   *  Minion sprites typically come from ENEMY_SPRITES (creatures: skeleton, phoenix, etc.).
   */
  private addSummonedAlly(data: { id: string; spriteKey: string; name: string; hp: number; maxHp: number; ownerId?: string; defensiveTypes?: string[] }): void {
    const config = ENEMY_SPRITES[data.spriteKey] || PLAYER_SPRITES[data.spriteKey] || PLAYER_SPRITES.warrior;

    // Determine insertion index — immediately right of host (default to end if host not found)
    const hostIdx = data.ownerId
      ? this.players.findIndex(p => p.id === data.ownerId)
      : -1;
    const insertIdx = hostIdx >= 0 ? hostIdx + 1 : this.sceneData.players.length;

    // Insert minion def at the adjacent slot
    this.sceneData.players.splice(insertIdx, 0, {
      id: data.id,
      spriteKey: data.spriteKey,
      name: data.name,
      hp: data.hp,
      maxHp: data.maxHp,
    });

    // Recalculate all player formation positions for the new count.
    // Track which configs are minions (ENEMY_SPRITES sourced) so we can boost their scale —
    // backline-tuned minion sprites read as babies next to full-size strands otherwise.
    const playerConfigs = [
      ...this.players.slice(0, insertIdx).map(p => p.config),
      config,
      ...this.players.slice(insertIdx).map(p => p.config),
    ];
    const playerIsMinion = [
      ...this.sceneData.players.slice(0, insertIdx).map(p => !PLAYER_SPRITES[p.spriteKey]),
      true,
      ...this.sceneData.players.slice(insertIdx + 1).map(p => !PLAYER_SPRITES[p.spriteKey]),
    ];
    const playerPositions = this.layout.getFormationPositions(
      'player',
      this.sceneData.players.length,
      playerConfigs.map((c, i) => c.scale * (playerIsMinion[i] ? MINION_SCALE_BOOST : 1)),
    );

    // Tween existing players to their new positions (skipping the insertion slot)
    this.players.forEach((player, i) => {
      const newSlot = i < insertIdx ? i : i + 1;
      const pos = playerPositions[newSlot];
      this.tweens.add({
        targets: player.sprite,
        x: pos.x,
        y: pos.y,
        duration: 300,
        ease: 'Power2',
      });
      player.sprite.setScale(pos.scale);
      player.sprite.setDepth(pos.depth);
      player.sprite.setData('breathBaseScale', pos.scale);
      this.updateShadow(this.playerShadows[i], pos, player.sprite.displayWidth, player.config);
    });

    // Sprite is visible immediately; entrance preset handles the materialization beat.
    const newPos = playerPositions[insertIdx];

    const sprite = this.add.sprite(newPos.x, newPos.y, config.smKey);
    sprite.setOrigin(0.5, 1.0);
    sprite.setScale(newPos.scale);
    sprite.setDepth(newPos.depth);
    sprite.setData('baseDisplayHeight', this.textures.get(config.smKey).getSourceImage().height * newPos.scale);
    sprite.setData('baseScale', newPos.scale);

    this.players.splice(insertIdx, 0, { sprite, id: data.id, isEnemy: false, config, isMinion: true });

    this.startIdleBreath(sprite, newPos.scale);
    this.applyBaseOutline(sprite);

    // Player-side facing — minions face enemies (right). flipFacing flag handled by updateSpriteFacing.
    sprite.setFlipX(!!config.flipFacing);

    // Create shadow with alpha 0
    const shadow = this.createShadowAt(newPos, sprite.displayWidth, config);
    shadow.setAlpha(0);
    this.playerShadows.splice(insertIdx, 0, shadow);

    // Apply ambient lighting
    const tint = CombatScene.LIGHTING_TINTS[this.lightingType];
    if (tint) {
      this.setLayer(sprite, 'lighting', tint, { noGlow: true });
    }

    // Shadow still fades in; sprite stays visible — entrance preset handles drama.
    this.tweens.add({
      targets: shadow,
      alpha: 1,
      duration: 400,
      ease: 'Power2',
      onComplete: () => {
        this.emitCombatantPositions();
      },
    });

    // Same entrance preset treatment as enemy summons (void portal for dark, rise otherwise).
    this.playSummonEntrance(sprite, data.defensiveTypes);
  }

  playDefendAnimation(): void {
    this.cameras.main.flash(150, 200, 168, 110, false);
  }

  playDefeatedAnimation(targetId?: string): void {
    const combatant = targetId ? this.findCombatant(targetId) : null;
    if (!combatant) return;

    const sprite = combatant.sprite;
    this.clearAllLayers(sprite);
    sprite.disableInteractive();

    // Clean up targeting state if this unit was highlighted
    if (targetId && this.highlightedIds.has(targetId)) {
      this.highlightedIds.delete(targetId);
    }

    const wasSummonedEnemy = combatant.isEnemy && combatant.isSummoned === true;

    this.tweens.add({
      targets: sprite,
      alpha: 0,
      y: sprite.y + 30,
      scaleX: 0.3,
      scaleY: 0.3,
      duration: 600,
      ease: 'Power2',
      onComplete: () => {
        sprite.setVisible(false);
        if (wasSummonedEnemy) {
          this.removeEnemyEntry(combatant);
        }
      },
    });

    // Fade out the shadow too
    const idx = combatant.isEnemy
      ? this.enemies.indexOf(combatant)
      : this.players.indexOf(combatant);
    const shadowArr = combatant.isEnemy ? this.enemyShadows : this.playerShadows;
    const shadow = idx >= 0 ? shadowArr[idx] : null;
    if (shadow) {
      this.tweens.add({
        targets: shadow,
        alpha: 0,
        duration: 600,
        ease: 'Power2',
      });
    }

    // When a summoned enemy dies, slide the remaining enemies (notably the boss)
    // back to their centered formation positions instead of leaving them drifted.
    // Runs in parallel with the death tween so it feels like a single beat.
    if (wasSummonedEnemy) {
      this.reflowLivingEnemies(combatant.id);
    }
  }

  /** Recompute formation positions for all enemies except the excluded id and tween
   *  survivors to their new slots. Called after a summoned enemy dies so the boss
   *  re-centers. Mirrors the spawn-side reflow inside addSummonedEnemy. */
  private reflowLivingEnemies(excludeId: string): void {
    const survivors = this.enemies.filter(e => e.id !== excludeId);
    if (survivors.length === 0) return;

    const positions = this.layout.getFormationPositions(
      'enemy',
      survivors.length,
      survivors.map(e => e.config.scale),
    );

    survivors.forEach((enemy, i) => {
      const pos = positions[i];
      this.tweens.add({
        targets: enemy.sprite,
        x: pos.x,
        y: pos.y,
        duration: 400,
        ease: 'Power2',
      });
      enemy.sprite.setScale(pos.scale);
      enemy.sprite.setDepth(pos.depth);
      enemy.sprite.setData('breathBaseScale', pos.scale);

      const origIdx = this.enemies.indexOf(enemy);
      const shadow = this.enemyShadows[origIdx];
      if (shadow) {
        this.updateShadow(shadow, pos, enemy.sprite.displayWidth, enemy.config);
      }
    });
  }

  /** Drop a dead summoned enemy from the arrays so it stops counting toward layout
   *  and future reflows. Player-side cleanup happens elsewhere — this only fires
   *  for enemy-side summons that just finished their death tween. */
  private removeEnemyEntry(combatant: CombatantSprite): void {
    const idx = this.enemies.indexOf(combatant);
    if (idx < 0) return;
    this.enemies.splice(idx, 1);
    const shadow = this.enemyShadows[idx];
    if (shadow) {
      shadow.destroy();
      this.enemyShadows.splice(idx, 1);
    }
    this.sceneData.enemies = this.sceneData.enemies.filter(e => e.id !== combatant.id);
    this.emitCombatantPositions();
  }

  private highlightTimelineUnit(combatantId: string | null): void {
    if (this.timelineHoveredId) {
      const prev = this.findCombatant(this.timelineHoveredId);
      if (prev) this.clearLayer(prev.sprite, 'targeting');
    }
    this.timelineHoveredId = combatantId;
    if (combatantId) {
      const entry = this.findCombatant(combatantId);
      if (entry) this.setLayer(entry.sprite, 'targeting', 0xffdd44, { glowOnly: true });
    }
  }

  // ---------------------------------------------------------------------------
  // Room transition — destroy enemies, keep players, create new enemies
  // ---------------------------------------------------------------------------

  private transitionRoom(newSceneData: CombatSceneData): void {
    // --- Clean up enemies only ---
    for (const enemy of this.enemies) {
      this.visualLayers.delete(enemy.sprite);
      // Remove targeting handlers for this enemy
      for (const key of [`target_${enemy.id}`, `hover_${enemy.id}`, `hoverend_${enemy.id}`]) {
        this.targetHandlers.delete(key);
      }
      enemy.sprite.destroy();
    }
    for (const shadow of this.enemyShadows) {
      shadow.destroy();
    }
    this.enemies = [];
    this.enemyShadows = [];
    this.highlightedIds.clear();
    this.highlightMode = null;
    this.hoveredEnemyId = null;

    // --- Update scene data ---
    this.sceneData = newSceneData;

    // Remap player IDs (safety — should be stable)
    for (let i = 0; i < this.players.length && i < newSceneData.players.length; i++) {
      this.players[i].id = newSceneData.players[i].id;
    }

    // --- Background swap if needed ---
    const newBgKey = newSceneData.bgKey ?? this.currentBgKey;
    if (newBgKey !== this.currentBgKey) {
      this.currentBgKey = newBgKey;
      const bg = BACKGROUNDS[newBgKey];
      if (bg) {
        if (this.textures.exists(bg.key)) {
          this.drawBackground(this.scale.width, this.scale.height);
        } else {
          const base = import.meta.env.BASE_URL;
          this.load.image(bg.key, `${base}${bg.path}`);
          this.load.once('complete', () => {
            this.drawBackground(this.scale.width, this.scale.height);
          });
          this.load.start();
        }
      }
    }

    // --- Lighting swap if needed ---
    const newLighting = newSceneData.lightingType ?? 'daylight';
    if (newLighting !== this.lightingType) {
      this.lightingType = newLighting;
      // Clear existing lighting on players, then reapply
      for (const p of this.players) {
        this.clearLayer(p.sprite, 'lighting');
      }
      this.applyAmbientLighting();
    }

    // --- Load missing enemy textures, then create sprites ---
    const { enemies: enemyDefs } = newSceneData;
    const enemyConfigs = enemyDefs.map(def => ENEMY_SPRITES[def.spriteKey] || ENEMY_SPRITES.skarn);

    // Check for textures not yet in the texture manager
    const base = import.meta.env.BASE_URL;
    let needsLoad = false;
    for (const config of enemyConfigs) {
      if (!this.textures.exists(config.smKey)) {
        this.load.image(config.smKey, `${base}${config.smPath}`);
        needsLoad = true;
      }
    }

    const createEnemies = () => {
      const enemyPositions = this.layout.getFormationPositions(
        'enemy',
        enemyDefs.length,
        enemyConfigs.map(c => c.scale),
      );

      enemyDefs.forEach((def, i) => {
        const config = enemyConfigs[i];
        const pos = enemyPositions[i];

        const sprite = this.add.sprite(pos.x, pos.y, config.smKey);
        sprite.setOrigin(0.5, 1.0);
        sprite.setScale(pos.scale);
        sprite.setDepth(pos.depth);
        sprite.setData('baseDisplayHeight', this.textures.get(config.smKey).getSourceImage().height * pos.scale);
        sprite.setData('baseScale', pos.scale);
      const isPortrait = window.innerWidth / window.innerHeight < 1;
      if (!isPortrait) {
        sprite.setInteractive({ useHandCursor: true });
        sprite.on('pointerdown', () => this.selectTarget(def.id));
        sprite.on('pointerover', () => this.onEnemyHover(def.id));
        sprite.on('pointerout', () => this.onEnemyHoverEnd(def.id));
      }

      this.enemies.push({ sprite, id: def.id, isEnemy: true, config });
      const shadow = this.createShadowAt(pos, sprite.displayWidth, config);
      this.enemyShadows.push(shadow);
      this.startIdleBreath(sprite, pos.scale);
      this.applyBaseOutline(sprite);

      // Fade in new enemies (room transition only)
      sprite.setAlpha(0);
      shadow.setAlpha(0);
      this.tweens.add({
        targets: sprite,
        alpha: 1,
        duration: 350,
        delay: i * 80,
        ease: 'Sine.easeOut',
      });
      this.tweens.add({
        targets: shadow,
        alpha: 1, // GameObject alpha multiplier; fillAlpha=SHADOW_ALPHA controls final darkness
        duration: 350,
        delay: i * 80,
        ease: 'Sine.easeOut',
      });
    });

      // Apply facing and ambient lighting to new enemies
      for (const enemy of this.enemies) {
        enemy.sprite.setFlipX(true);
      }
      const tint = CombatScene.LIGHTING_TINTS[this.lightingType];
      if (tint) {
        for (const enemy of this.enemies) {
          this.setLayer(enemy.sprite, 'lighting', tint, { noGlow: true });
        }
      }

      // --- Update active player highlight ---
      this.setActivePlayer(newSceneData.activeCombatantId ?? null);

      // --- Signal HUD to reinitialize ---
      EventBus.emit('COMBAT_SCENE_READY');
    };

    if (needsLoad) {
      this.load.once('complete', createEnemies);
      this.load.start();
    } else {
      createEnemies();
    }
  }

  // ---------------------------------------------------------------------------
  // Crawl celebration — golden particles + camera effects
  // ---------------------------------------------------------------------------

  private playCrawlCelebration(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    const cx = w / 2;
    const cy = h / 2;

    // Camera flash (golden)
    this.cameras.main.flash(300, 255, 204, 0);
    this.cameras.main.shake(200, 0.008);

    // Center burst — gold circles radiating outward
    const burstCount = 14;
    for (let i = 0; i < burstCount; i++) {
      const angle = (i / burstCount) * Math.PI * 2;
      const circle = this.add.circle(cx, cy, 6, 0xffcc00, 0.9);
      circle.setDepth(100);

      const dist = 120 + Math.random() * 80;
      this.tweens.add({
        targets: circle,
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0.3,
        duration: 600 + Math.random() * 200,
        ease: 'Power2',
        onComplete: () => circle.destroy(),
      });
    }

    // Rising shimmer — small gold/white circles floating upward
    const shimmerCount = 12;
    for (let i = 0; i < shimmerCount; i++) {
      const sx = Math.random() * w;
      const sy = h * 0.5 + Math.random() * (h * 0.4);
      const color = Math.random() > 0.4 ? 0xffcc00 : 0xffffff;
      const size = 3 + Math.random() * 4;
      const delay = Math.random() * 600;

      const dot = this.add.circle(sx, sy, size, color, 0);
      dot.setDepth(100);

      this.tweens.add({
        targets: dot,
        y: sy - 100 - Math.random() * 80,
        alpha: { from: 0.8, to: 0 },
        scale: { from: 1, to: 0.4 },
        duration: 1200 + Math.random() * 400,
        delay,
        ease: 'Power1',
        onComplete: () => dot.destroy(),
      });
    }

    // Signal DOM after celebration
    this.time.delayedCall(1200, () => {
      EventBus.emit(Events.CRAWL_CELEBRATE_DONE);
    });
  }

  shutdown(): void {
    this.init(); // reuse the same cleanup
    this.scale.off('resize', this.handleResize, this);
  }
}
